import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { ValidationMethod } from '@core/enums';
import { PasswordRequirement } from '@core/models';
import { ConstantsService, SysTextService } from '@core/services';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-password-validator',
    templateUrl: './password-validator.component.html',
    styleUrls: ['./password-validator.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PasswordValidatorComponent implements OnInit, OnChanges, OnDestroy {
  static readonly componentName = 'PasswordValidatorComponent';

  @Input() password: string;
  @Input() reqList: PasswordRequirement[];
  @Output() validationUpdate = new EventEmitter<boolean>();

  public sysText: any;

  private readonly template = 'passwordValidator';
  private asciiArray: number[];
  private destroy$: Subject<void> = new Subject<void>();

  constructor(
    private constants: ConstantsService,
    private textService: SysTextService
  ) {}

  ngOnInit(): void {
    // its own text, since it's shared by the sign-in pages and the change password dialog
    this.textService.getSysText(this.template)
      .pipe(takeUntil(this.destroy$))
      .subscribe((sysText) => this.sysText = sysText);

    this.runValidationChecks();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(): void {
    this.runValidationChecks();
  }

  private runValidationChecks(): void {
    this.asciiArray = this.password.split('').map(char => char.charCodeAt(0));

    this.reqList.forEach(req => {
      if (req.validationMethodId === ValidationMethod.MinLength) {
        this.minLengthCheck(req.value);
      } else if (req.validationMethodId === ValidationMethod.UpperCase) {
        this.upperCaseCheck(req.value);
      } else if (req.validationMethodId === ValidationMethod.LowerCase) {
        this.lowerCaseCheck(req.value);
      } else if (req.validationMethodId === ValidationMethod.Number) {
        this.numberCheck(req.value);
      } else if (req.validationMethodId === ValidationMethod.SpecialChar){
        this.specialCharCheck(req.value);
      }
    });
  }

  private minLengthCheck(value: number): void {
    const result = this.password.length >= value ? true : false;
    this.updateRequirements(ValidationMethod.MinLength, result);
  }

  private lowerCaseCheck(value: number): void {
    const result = this.countOccurances(this.constants.LOWERCASE_ASCII_RANGES) >= value ? true : false;
    this.updateRequirements(ValidationMethod.LowerCase, result);
  }

  private upperCaseCheck(value: number): void {
    const result = this.countOccurances(this.constants.UPPERCASE_ASCII_RANGES) >= value ? true : false;
    this.updateRequirements(ValidationMethod.UpperCase, result);
  }

  private numberCheck(value: number): void {
    const result = this.countOccurances(this.constants.NUMBER_ASCII_RANGES) >= value ? true : false;
    this.updateRequirements(ValidationMethod.Number, result);
  }

  private specialCharCheck(value: number): void {
    const result = this.countOccurances(this.constants.SPECIALCHAR_ASCII_RANGES) >= value ? true : false;
    this.updateRequirements(ValidationMethod.SpecialChar, result);
  }

  private countOccurances(rangeArr: { min: number; max: number }[]): number {
    let count = 0;
    rangeArr.forEach(range => {
      count += this.asciiArray.reduce((n, x) => n + (x >= range.min && x <= range.max ? 1 : 0), 0);
    });
    return count;
  }

  private updateRequirements(vmId: number, status: boolean): void {
    const requirementIndex = this.reqList.findIndex(req => req.validationMethodId === vmId);
    this.reqList[requirementIndex].valid = status;
    this.validationUpdate.emit(this.allReqsMet());
  }

  private allReqsMet(): boolean {
    return this.reqList.every(req => req.valid);
  }
}
